import type { OptionsDocument, QuoteDocument } from 'paintscout'
import type { UpdateableLineItem } from '..'
import type { QuoteItemSection, UpdateableProduct } from '../../../index'
import { createGroup, updateLineItem, getGroupDetails } from '../../../index'
import { getItems } from '../../items/get-items'
import { createLineItem } from '../create-line-item'
import union from 'lodash/union'
import difference from 'lodash/difference'

export function convertToLineItem(args: {
  quote: QuoteDocument
  options: OptionsDocument
  itemKeys: string[]
  products?: UpdateableProduct[]
  section?: QuoteItemSection
  hideDescriptionKeys?: string[]
}): UpdateableLineItem {
  const { quote, options, itemKeys, section, hideDescriptionKeys, products = [] } = args
  // Create and add a temp group to the quote to build this line item description from
  const tempGroupQuote = {
    ...quote,
    groups: {
      ...quote.groups,
      ['tempGroup']: createGroup({
        quote,
        clientOptions: options,
        keys: itemKeys,
        section
      })
    }
  }

  const { price, description, files, hours } = getGroupDetails({
    quote: tempGroupQuote,
    options,
    keys: itemKeys,
    groupKey: 'tempGroup',
    section,
    hideDescriptionKeys
  })

  const materialsPrice = products.reduce((acc, current) => {
    return acc + (current.totalPrice.useCustom ? current.totalPrice.custom : current.totalPrice.default)
  }, 0)
  const laborPrice = products.length > 0 ? price - materialsPrice : price

  const res = {
    ...createLineItem({ section }),
    materials: products,
    price: laborPrice,
    totals: {
      materials: materialsPrice,
      afterMaterials: laborPrice + materialsPrice
    },
    description,
    files,
    hours
  }

  return res
}

export function convertTableToLineItem(args: {
  quote: QuoteDocument
  options?: OptionsDocument
  section: QuoteItemSection
  priceOverride?: number
}): QuoteDocument {
  const { quote, options, section, priceOverride } = args
  const view = quote.defaultView === 'substrate' ? 'substrate' : 'area'
  const customerItemKeys = getItems({
    quote,
    options,
    section: section,
    consumer: 'customer',
    view
  }).map((item) => item.key)

  const crewItemKeys = getItems({
    quote,
    options,
    section: section,
    consumer: 'crew',
    view
  }).map((item) => item.key)

  const itemsHiddenFromCustomer = difference(crewItemKeys, customerItemKeys)
  const itemKeys = union(customerItemKeys, crewItemKeys)

  if (itemKeys.length === 0) {
    return quote
  }

  const converted = convertToLineItem({
    quote,
    options,
    itemKeys,
    section,
    hideDescriptionKeys: itemsHiddenFromCustomer
  })

  const purgedQuote: QuoteDocument = {
    ...quote,
    areas: {},
    substrates: {},
    lineItems: {},
    groups: {}
  }

  return updateLineItem({
    quote: purgedQuote,
    item: {
      ...converted,
      price: priceOverride ?? converted.price
    },
    options,
    insertAfterKey: itemKeys[0],
    calculate: true
  })
}

export function convertTablesToLineItem(args: {
  quote: QuoteDocument
  options?: OptionsDocument
  sections: QuoteItemSection[]
  priceOverride?: number
}): QuoteDocument {
  const { quote, options, sections, priceOverride } = args

  return sections.reduce((acc, section) => {
    return convertTableToLineItem({
      quote: acc,
      options,
      section: section as QuoteItemSection,
      priceOverride: section === 'additional' ? 0 : priceOverride
    })
  }, quote)
}
